import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    alpha,
    Box,
    Button,
    CircularProgress,
    Container,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { buildApiUrl } from "../../config/api";
import { APP_NAME } from "../../config/brand";
import { authSuccess } from "../../redux/userRelated/userSlice";
import { getDashboardPathForRole } from "../../utils/authRoutes";
import { getPlanById, TRIAL_DAYS } from "../../config/subscriptionPlans";
import { resolveSchoolVerificationStatus } from "../../utils/schoolVerification";

const POLL_MS = 30000;

export default function PendingVerificationPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentUser } = useSelector((state) => state.user);
    const [checking, setChecking] = useState(false);

    const schoolId = currentUser?._id || currentUser?.id;
    const schoolName = currentUser?.schoolName || "Your school";
    const planName = getPlanById(currentUser?.subscriptionPlan).name;

    const refreshStatus = useCallback(async () => {
        if (!schoolId) return;
        setChecking(true);
        try {
            const { data } = await axios.get(buildApiUrl(`/Admin/${schoolId}`));
            const status = resolveSchoolVerificationStatus(data);

            if (status === "approved") {
                dispatch(authSuccess({ ...currentUser, ...data }));
                navigate(getDashboardPathForRole("Admin", data), { replace: true });
                return;
            }

            if (status === "rejected") {
                navigate("/login", {
                    replace: true,
                    state: {
                        message:
                            "Your school registration was not approved. Please contact platform support.",
                    },
                });
            }
        } catch {
            // Keep showing the waiting screen; user can retry manually.
        } finally {
            setChecking(false);
        }
    }, [currentUser, dispatch, navigate, schoolId]);

    useEffect(() => {
        if (!schoolId) return undefined;
        const timer = setInterval(refreshStatus, POLL_MS);
        return () => clearInterval(timer);
    }, [refreshStatus, schoolId]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                py: 4,
            }}
        >
            <Container maxWidth="sm">
                <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
                    <Stack spacing={3} alignItems="center" textAlign="center">
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
                                color: "warning.main",
                            }}
                        >
                            <HourglassTopOutlinedIcon sx={{ fontSize: 40 }} />
                        </Box>

                        <Box>
                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                Awaiting verification
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                <strong>{schoolName}</strong> has been registered on {APP_NAME}. A system
                                administrator will review your school before your {TRIAL_DAYS}-day free trial on
                                the <strong>{planName}</strong> plan begins.
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                            You cannot access the director dashboard until verification is complete. This page
                            checks automatically every 30 seconds and will open your dashboard once approved.
                        </Typography>

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ width: "100%" }}>
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={checking}
                                onClick={refreshStatus}
                            >
                                {checking ? <CircularProgress size={22} color="inherit" /> : "Check status now"}
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="inherit"
                                component={RouterLink}
                                to="/logout"
                                startIcon={<LogoutOutlinedIcon />}
                            >
                                Sign out
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}
